// src/dailyCodes.js

/** @typedef {{code:string, dir?:string|null, mean?:number|null, target?:number|null, pr?:number|null}} LevelMeta */
/** @typedef {{isoDateUTC:string, levels:{1:LevelMeta,2:LevelMeta,3:LevelMeta}, global:{mode?:string|null,tol?:number|null,lambda?:number|null,rng_seed?:string|null,version?:string|null,created_at?:string|null}}} DailyCodes */

const memoryCache = new Map();

// Local dev must use CRA proxy (same-origin); mobile must use absolute production URL.
const isNative = typeof window !== 'undefined' && (!!window.Capacitor || window.location.protocol === 'capacitor:');
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const PROD_URL = "https://crackle.theaiwhisperer.agency";
const API_BASE = isNative ? PROD_URL : (isLocalhost ? "" : (process.env.REACT_APP_API_BASE || ""));

/** Join base + path safely (no double /api, no double slashes). */
function api(path) {
  if (!API_BASE) return path;
  return API_BASE.replace(/\/$/, "") + path;
}

async function getJson(res) {
  const ct = res.headers.get("content-type") || "";
  const body = await res.text(); // read once
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  if (!ct.includes("application/json")) {
    throw new Error(`Expected JSON, got ${ct || "unknown"} (first 80: ${body.slice(0, 80)})`);
  }
  try { return JSON.parse(body); }
  catch { throw new Error("Bad JSON from server"); }
}

/** Get YYYY-MM-DD in America/New_York timezone. */
export function getESTDateString(d = new Date()) {
  return d.toLocaleDateString('sv-SE', { timeZone: 'America/New_York' });
}

function toIsoDateUTC(d = new Date()) {
  return getESTDateString(d); // Aliased for backward compatibility in this file
}

function readCache(key) {
  return memoryCache.get(key);
}
function writeCache(key, value) {
  memoryCache.set(key, value);
}

function pickCode(server, primaryKey, legacyKey) {
  return server?.[primaryKey] ?? server?.[legacyKey];
}

function normalize(server) {
  const isoDateUTC = (server?.date || "").slice(0, 10) || toIsoDateUTC();

  // Handle your current API structure
  const L1 = {
    code: String(server?.l1 || pickCode(server, "l1_code", "11") || ""),
    dir: null,
    pr: server?.l1_pr4 ?? null,
    mean: server?.l1_mean ?? null,
    target: null,
  };
  const L2 = {
    code: String(server?.l2 || pickCode(server, "l2_code", "12") || ""),
    dir: server?.l2_dir ?? null,
    pr: server?.l2_pr5 ?? null,
    mean: server?.l2_mean ?? null,
    target: server?.l2_target ?? null,
  };
  const L3 = {
    code: String(server?.l3 || pickCode(server, "l3_code", "13") || ""),
    dir: server?.l3_dir ?? null,
    pr: server?.l3_pr6 ?? null,
    mean: server?.l3_mean ?? null,
    target: server?.l3_target ?? null,
  };

  [L1, L2, L3].forEach((lv, i) => {
    if (!/^[0-9A-F]{5}$/i.test(lv.code)) throw new Error(`Invalid code for level ${i + 1}: "${lv.code}"`);
    lv.code = lv.code.toUpperCase();
  });

  const global = {
    mode: server?.mode ?? null,
    tol: server?.tol ?? null,
    lambda: server?.lambda ?? null,
    rng_seed: server?.rng_seed ?? null,
    version: server?.version ?? null,
    created_at: server?.created_at ?? null,
  };

  return { isoDateUTC, levels: { 0: L1, 1: L2, 2: L3 }, global };
}

// Try date endpoint first (if given), else today
async function fetchBundle(dateUTC) {
  const isNative = typeof window !== 'undefined' && (!!window.Capacitor || window.location.protocol === 'capacitor:');
  const fetchOptions = {
    method: "GET",
    ...(isNative ? {} : { credentials: "include" }),
  };

  if (dateUTC) {
    const byDate = await fetch(api(`/api/get_codes?date=${encodeURIComponent(dateUTC)}`), fetchOptions);
    if (byDate.status === 404) {
      throw new Error(`No codes available for ${dateUTC}`);
    
    } else {
      const data = await getJson(byDate);
      return normalize(data);
    }
  }

  const todayRes = await fetch(api("/api/get_todays_codes"), fetchOptions);
  if (todayRes.status === 404) {
    throw new Error('No codes available for today');
  
  }

  const data = await getJson(todayRes);
  return normalize(data);
}

/** Choose a random UTC date in the last N days (excludes today). */
function pickRandomPastDateUTC(daysBack = 180) {
  const now = new Date();
  const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  // range: 1..daysBack days ago
  const offsetDays = 1 + Math.floor(Math.random() * Math.max(1, daysBack));
  const d = new Date(todayUTC);
  d.setUTCDate(d.getUTCDate() - offsetDays);
  return toIsoDateUTC(d);
}

/** Get bundle for a specific date (or today EST if omitted). */
export async function getDailyCodes(dateUTC) {
  const cacheKey = dateUTC || getESTDateString();
  const cached = readCache(cacheKey);
  if (cached) return cached;

  const normalized = await fetchBundle(dateUTC || cacheKey);
  if (dateUTC && normalized.isoDateUTC !== dateUTC) {
    throw new Error(`Code date mismatch (requested ${dateUTC}, got ${normalized.isoDateUTC})`);
  }

  // Store under the server-declared date to keep cache consistent with source of truth
  writeCache(normalized.isoDateUTC, normalized);
  if (normalized.isoDateUTC !== cacheKey) {
    writeCache(cacheKey, normalized);
  }
  return normalized;
}

// Always go through the normalized path:
// - today   → /api/get_todays_codes
// - by date → /api/get_codes?date=YYYY-MM-DD
export async function getServerCodes(useRandom = false) {
  const date = useRandom ? pickRandomPastDateUTC() : undefined;
  return getDailyCodes(date);
}

/** Keep single-level helper if you still want it. */
export async function getDailyCode(level, dateUTC) {
  const bundle = await getDailyCodes(dateUTC);
  return bundle.levels[level].code;
}
